import type { SubjectTrackView } from "@sitescene/types";

import { TrackDirection, TrackHopState } from "@sitescene/types";
import { useEffect, useState } from "react";

import { DetailPanel } from "@/components/detail-panel";
import { Button } from "@/components/ui/buttons/button";
import {
	formatClock,
	formatScore,
	formatSpan,
	secondsBetween,
} from "@/features/tracking/format";
import { SightingPlayer } from "@/features/tracking/sighting-player";
import { useTrackActions } from "@/features/tracking/use-tracks";

interface ReviewModeProps {
	onClose: () => void;
	track: SubjectTrackView;
}

/**
 * Steps through the sightings waiting for review, one at a time and in time
 * order: the person from the event beside the candidate, the candidate's
 * clip with them boxed, and Gemini's reason. Y says same person, N says not
 * them, → skips. A decision moves straight on; the track recomputes in the
 * background, so sightings it newly sends to review join the end.
 */
export const ReviewMode = ({ onClose, track }: ReviewModeProps) => {
	const { review } = useTrackActions(track.id);
	// Settled or skipped here, so the queue moves on before the track recomputes
	const [decided, setDecided] = useState<ReadonlySet<string>>(new Set());
	const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
	const [failure, setFailure] = useState<string>();

	// Not the ones Gemini is still looking at: its answer may settle them
	const checking = new Set(track.expansionProgress?.checking ?? []);
	const waiting = track.hops
		.filter(
			(hop) =>
				hop.state === TrackHopState.NeedsReview &&
				!decided.has(hop.tracklet) &&
				!checking.has(hop.tracklet) &&
				track.sightings[hop.tracklet],
		)
		.sort(
			(a, b) =>
				new Date(a.startTimestamp).getTime() -
				new Date(b.startTimestamp).getTime(),
		);
	const queue = waiting.filter((hop) => !skipped.has(hop.tracklet));
	const hop = queue[0];
	const sighting = hop ? track.sightings[hop.tracklet] : undefined;
	const seedCrops = track.seedTracklets.flatMap(
		(id) => track.sightings[id]?.cropUrls ?? [],
	);

	const decide = (confirmed: boolean) => {
		if (!hop) return;
		const tracklet = hop.tracklet;
		setFailure(undefined);
		setDecided((current) => new Set(current).add(tracklet));
		review.mutate(
			{ confirmed, tracklet },
			{
				onError: (error) => {
					setFailure(error.message);
					setDecided((current) => {
						const next = new Set(current);
						next.delete(tracklet);
						return next;
					});
				},
			},
		);
	};
	const skip = () => {
		if (hop) setSkipped((current) => new Set(current).add(hop.tracklet));
	};

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.metaKey || event.ctrlKey || event.altKey) return;
			const target = event.target as HTMLElement | null;
			if (target?.closest("input, textarea, select, [contenteditable]")) return;
			if (event.key === "Escape") onClose();
			else if (event.key === "y" || event.key === "Y") decide(true);
			else if (event.key === "n" || event.key === "N") decide(false);
			else if (event.key === "ArrowRight") skip();
			else return;
			event.preventDefault();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	});

	return (
		<DetailPanel title="Review sightings">
			<div className="flex flex-col gap-4">
				<div className="flex flex-wrap items-center justify-between gap-3 text-sm">
					<p className="text-secondary">
						{queue.length === 0
							? "Nothing left to review."
							: `${queue.length} left${skipped.size ? `, ${waiting.length - queue.length} skipped` : ""}. Y: same person · N: not them · →: skip · Esc: back`}
					</p>
					<div className="flex gap-2">
						{queue.length === 0 && waiting.length > 0 && (
							<Button
								color="secondary"
								onClick={() => setSkipped(new Set())}
								size="sm"
							>
								Review the {waiting.length} skipped
							</Button>
						)}
						<Button color="secondary" onClick={onClose} size="sm">
							Back to the timeline
						</Button>
					</div>
				</div>

				{failure && (
					<p className="text-sm text-error-primary" role="alert">
						{failure}
					</p>
				)}

				{hop && sighting && (
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
						<section className="flex min-w-0 flex-col gap-2">
							<h4 className="text-sm font-semibold text-primary">
								The person from the event
							</h4>
							<div className="flex flex-wrap gap-2">
								{seedCrops.map((url) => (
									<img
										alt=""
										className="h-40 rounded border border-secondary bg-gray-900 object-contain"
										key={url}
										src={url}
									/>
								))}
							</div>
						</section>

						<section className="flex min-w-0 flex-col gap-2">
							<h4 className="text-sm font-semibold text-primary">
								{sighting.sensor.name} · {formatClock(sighting.startTimestamp)}{" "}
								<span className="font-normal text-tertiary">
									(
									{formatSpan(
										secondsBetween(
											sighting.startTimestamp,
											sighting.endTimestamp,
										),
									)}
									,{" "}
									{hop.direction === TrackDirection.Backward
										? "before"
										: "after"}{" "}
									the event)
								</span>
							</h4>
							<div className="flex flex-wrap gap-2">
								{sighting.cropUrls.map((url) => (
									<img
										alt=""
										className="h-40 rounded border border-secondary bg-gray-900 object-contain"
										key={url}
										src={url}
									/>
								))}
							</div>
							<SightingPlayer key={sighting.tracklet} sighting={sighting} />
							{hop.score.verifier && (
								<p className="text-sm text-secondary">
									Gemini said{" "}
									<span className="font-medium">
										{hop.score.verifier.answer}
									</span>
									: {hop.score.verifier.reason}
								</p>
							)}
							<p className="text-xs text-tertiary tabular-nums">
								{hop.score.crossModality
									? "Joined on timing across color and grayscale"
									: `Appearance ${formatScore(hop.score.appearance)} · topology ${formatScore(hop.score.topology)} · margin ${formatScore(hop.score.margin)} · combined ${formatScore(hop.score.combined)}`}
							</p>
							<div className="flex flex-wrap gap-2">
								<Button onClick={() => decide(true)} size="sm">
									Same person (Y)
								</Button>
								<Button
									color="secondary"
									onClick={() => decide(false)}
									size="sm"
								>
									Not them (N)
								</Button>
								<Button color="tertiary" onClick={skip} size="sm">
									Skip (→)
								</Button>
							</div>
						</section>
					</div>
				)}
			</div>
		</DetailPanel>
	);
};
